import { computed, ref } from 'vue';

import { useState } from '~coreModules/core/js/composables/vuexHelpers';
import { useMediaQueries } from '~coreModules/core/js/composables/useMediaQueries';
import { useResizeObserver } from '~coreModules/core/js/composables/useResizeObserver';

import { HEADER_MODULE_NAME } from '~coreModules/header/js/header-store';
import { BROWSER_MODULE_NAME } from '~coreModules/browser/js/browser-store';

export function useTopOffset() {
    const { headerHeight } = useState(HEADER_MODULE_NAME, {
        headerHeight: state => state.headerHeight,
    });

    const { clientHeight } = useState(BROWSER_MODULE_NAME, {
        clientHeight: state => state.clientHeight,
    });

    function getTopOffset(elementHeight) {
        if (elementHeight < clientHeight.value - headerHeight.value) {
            return headerHeight.value + 20;
        }
        return (elementHeight - clientHeight.value) * -1;
    }

    return { getTopOffset };
}

/**
 * @description
 * A composable that calculates the top offset values for two elements (left and right columns)
 * based on their heights, the viewport height, and the header height, to achieve the desired sticky effect
 * when scrolling the PDP page. It dynamically updates the offsets when the elements are resized
 * or when media query conditions change.
 *
 * @param {HTMLElement} leftElement - reference to left column element
 * @param {HTMLElement} rightElement - reference to right column element

 * @returns {Object} object containing two computed properties for the top offsets of the left and right columns
 */
export default function usePDPColumnOffset(leftElement, rightElement) {
    const mediaQueries = useMediaQueries();
    const { getTopOffset } = useTopOffset();

    const leftElementHeight = ref(0);
    const rightElementHeight = ref(0);

    useResizeObserver(leftElement, (entry) => {
        leftElementHeight.value = entry.contentRect.height;
    });
    useResizeObserver(rightElement, (entry) => {
        rightElementHeight.value = entry.contentRect.height;
    });

    const leftTop = computed(() => (mediaQueries.isLargish ? getTopOffset(leftElementHeight.value) : 0));
    const rightTop = computed(() => (mediaQueries.isLargish ?
        getTopOffset(rightElementHeight.value) : 0));

    return { leftTop, rightTop };
}
